import {
  Dropdown,
  TextField,
  Checkbox,
  DefaultButton,
  PrimaryButton,
  BaseButton,
  Button,
} from "@fluentui/react";
import { styles } from "./rolloutDetailsForm.styles";
import { injectWiggleAnimation } from "../../../Shared/shared.styles";
import { MouseEventHandler } from "react";

// Helper to parse rollout type string (e.g., "PROD_SDP" or "PROD_SDP_Manual")
export function parseRolloutType(value: string) {
  const [environment, mode, progression] = value.split("_");
  return { environment, mode, progression };
}

export function parseStageType(filename: string) {
  const parts = filename.split(".");
  const stageType = parts[1];
  return stageType || "unknown"; // Default to "unknown" if no stage type found
}

const parseRolloutAndProgressionTypes = (param: any) => {
  // Extract options from param.values if present and is an array of strings
  let envOptions: { key: string; text: string }[] = [];
  let modeOptions: { key: string; text: string }[] = [];
  let progressionOptions: { key: string; text: string }[] = [];
  if (Array.isArray(param.values)) {
    // Try to infer env/mode/progression from values like ENV_MODE or ENV_MODE_PROGRESSION
    const envSet = new Set<string>();
    const modeSet = new Set<string>();
    const progressionSet = new Set<string>();
    param.values.forEach((v: string) => {
      const parts = v.split("_");
      if (parts[0]) envSet.add(parts[0]);
      if (parts[1]) modeSet.add(parts[1]);
      if (parts[2]) progressionSet.add(parts[2]);
    });
    envOptions = Array.from(envSet).map((e) => ({
      key: e,
      text: e,
    }));
    modeOptions = Array.from(modeSet).map((m) => ({
      key: m,
      text: m,
    }));
    progressionOptions = [
      {
        key: "Automatic",
        text: "Automatic",
      },
      ...Array.from(progressionSet)
        .filter(Boolean)
        .map((p) => ({ key: p, text: p })),
    ];
  }
  return {
    envOptions,
    modeOptions,
    progressionOptions,
  };
};

// Renders Environment, Mode, and Progression dropdowns
export function renderRolloutTypeDropdowns({
  value,
  onChange,
  envOptions,
  modeOptions,
  progressionOptions,
}: {
  value: string;
  onChange: (newValue: string) => void;
  envOptions: { key: string; text: string }[];
  modeOptions: { key: string; text: string }[];
  progressionOptions: { key: string; text: string }[];
}) {
  const {
    environment,
    mode,
    progression = "Automatic",
  } = parseRolloutType(value || "");

  // Validation: Manual is only allowed when mode is SDP
  const isManualInvalid = progression === "Manual" && mode !== "SDP";
  const errorMessage = isManualInvalid
    ? `Manual is not applicable to mode ${mode}.`
    : undefined;

  // Compose new rollout type string
  const update = (env: string, m: string, prog?: string) => {
    let val = `${env}_${m}`;
    if (prog) val += `_${prog}`;
    onChange(val);
  };

  return (
    <div style={{ display: "flex", gap: 16 }}>
      <Dropdown
        label="Environment"
        options={envOptions}
        selectedKey={environment}
        onChange={(_, o) => update(o?.key as string, mode, progression)}
        styles={{ dropdown: { width: 140 } }}
      />
      <Dropdown
        label="Mode"
        options={modeOptions}
        selectedKey={mode}
        onChange={(_, o) => update(environment, o?.key as string, progression)}
        styles={{ dropdown: { width: 140 } }}
      />
      <Dropdown
        label="Progression"
        options={progressionOptions}
        selectedKey={progression}
        onChange={(_, o) => update(environment, mode, o?.key as string)}
        styles={{ dropdown: { width: 140 } }}
        errorMessage={errorMessage}
      />
    </div>
  );
}

// Renders a single form field for a rollout parameter
export function renderField(
  param: any,
  value: any,
  onChange: (name: string, value: any) => void,
) {
  if (param.name === "RolloutAndProgressionType") {
    const { envOptions, modeOptions, progressionOptions } =
      parseRolloutAndProgressionTypes(param);
    return renderRolloutTypeDropdowns({
      value,
      onChange: (v: any) => onChange(param.name, v),
      envOptions,
      modeOptions,
      progressionOptions,
    });
  }

  if (param.type === "boolean") {
    return (
      <Checkbox
        key={param.name}
        label={param.displayName || param.name}
        checked={!!value}
        onChange={(_, checked) => onChange(param.name, checked)}
      />
    );
  }
  if (param.type === "string" && Array.isArray(param.values)) {
    return (
      <Dropdown
        key={param.name}
        label={param.displayName || param.name}
        options={param.values.map((v: string) => ({ key: v, text: v }))}
        selectedKey={value}
        onChange={(_, option) => onChange(param.name, option?.key)}
      />
    );
  }
  // Default: string input
  return (
    <TextField
      key={param.name}
      label={param.displayName || param.name}
      value={value ?? ""}
      onChange={(_, v) => onChange(param.name, v)}
    />
  );
}

export const getRolloutFooter = (
  handleCancel:
    | MouseEventHandler<
        | HTMLDivElement
        | HTMLAnchorElement
        | HTMLButtonElement
        | BaseButton
        | Button
        | HTMLSpanElement
      >
    | undefined,
  handleStartExperiment:
    | MouseEventHandler<
        | HTMLDivElement
        | HTMLAnchorElement
        | HTMLButtonElement
        | BaseButton
        | Button
        | HTMLSpanElement
      >
    | undefined,
  startWiggle: boolean,
) => (
  <div className={styles.rolloutDetailsFormActions}>
    <DefaultButton onClick={handleCancel}>Cancel</DefaultButton>
    <PrimaryButton
      onClick={handleStartExperiment}
      style={startWiggle ? injectWiggleAnimation : undefined}
    >
      Start
    </PrimaryButton>
  </div>
);
